import React, { forwardRef, useId } from "react";
import { AlertCircle, ChevronDown, Search, X } from "lucide-react";
import { cx } from "./format";

const fieldBase =
  "w-full rounded-cm border bg-cm-surface-2 text-cm-text text-sm placeholder:text-cm-faint transition-shadow " +
  "focus:outline-none focus:border-cm-gold focus:shadow-cm-focus disabled:opacity-60 disabled:cursor-not-allowed";

const sizeCls = { sm: "h-8 px-2.5 text-[13px]", md: "h-10 px-3", lg: "h-12 px-4 text-[15px]" };

/** Label above, help or error below. Wrap any control. */
export const FormField = ({ label, htmlFor, help, error, required, className, children }) => (
  <div className={cx("flex flex-col gap-1.5 min-w-0", className)}>
    {label && (
      <label htmlFor={htmlFor} className="text-[13px] font-semibold text-cm-text">
        {label}
        {required && <span className="text-cm-danger ml-0.5" aria-hidden="true">*</span>}
      </label>
    )}
    {children}
    {error ? (
      <span className="flex items-center gap-1.5 text-xs text-cm-danger" role="alert">
        <AlertCircle size={13} aria-hidden="true" />
        {error}
      </span>
    ) : help ? (
      <span className="text-xs text-cm-muted">{help}</span>
    ) : null}
  </div>
);

/** Text input. `prefix` (e.g. "₹" or "+91") sits inside the field on the left. */
export const TextInput = forwardRef(function TextInput(
  { label, help, error, required, prefix, suffix, size = "md", className, inputClassName, id, ...rest },
  ref,
) {
  const autoId = useId();
  const fieldId = id || autoId;
  const input = (
    <div className="relative">
      {prefix && (
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-cm-muted pointer-events-none">{prefix}</span>
      )}
      <input
        ref={ref}
        id={fieldId}
        aria-invalid={error ? true : undefined}
        className={cx(
          fieldBase,
          sizeCls[size],
          error ? "border-cm-danger" : "border-cm-border-strong",
          prefix && (String(prefix).length > 1 ? "pl-11" : "pl-7"),
          suffix && "pr-10",
          inputClassName,
        )}
        {...rest}
      />
      {suffix && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-cm-muted">{suffix}</span>}
    </div>
  );
  if (!label && !help && !error) return <div className={className}>{input}</div>;
  return (
    <FormField label={label} htmlFor={fieldId} help={help} error={error} required={required} className={className}>
      {input}
    </FormField>
  );
});

export const Textarea = forwardRef(function Textarea(
  { label, help, error, required, className, rows = 3, id, ...rest },
  ref,
) {
  const autoId = useId();
  const fieldId = id || autoId;
  const el = (
    <textarea
      ref={ref}
      id={fieldId}
      rows={rows}
      className={cx(fieldBase, "py-2 px-3 min-h-[80px]", error ? "border-cm-danger" : "border-cm-border-strong")}
      {...rest}
    />
  );
  if (!label && !help && !error) return <div className={className}>{el}</div>;
  return (
    <FormField label={label} htmlFor={fieldId} help={help} error={error} required={required} className={className}>
      {el}
    </FormField>
  );
});

/**
 * Native select, styled. `options`: [{ value, label }] or strings.
 * `placeholder` adds an empty first option.
 */
export const SelectInput = forwardRef(function SelectInput(
  { label, help, error, required, options = [], placeholder, size = "md", className, id, children, ...rest },
  ref,
) {
  const autoId = useId();
  const fieldId = id || autoId;
  const el = (
    <div className="relative">
      <select
        ref={ref}
        id={fieldId}
        className={cx(fieldBase, sizeCls[size], "appearance-none pr-9 cursor-pointer", error ? "border-cm-danger" : "border-cm-border-strong")}
        {...rest}
      >
        {placeholder !== undefined && <option value="">{placeholder}</option>}
        {children ||
          options.map((o) => {
            const opt = typeof o === "object" ? o : { value: o, label: o };
            return (
              <option key={opt.value} value={opt.value} disabled={opt.disabled}>
                {opt.label}
              </option>
            );
          })}
      </select>
      <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-cm-muted pointer-events-none" />
    </div>
  );
  if (!label && !help && !error) return <div className={className}>{el}</div>;
  return (
    <FormField label={label} htmlFor={fieldId} help={help} error={error} required={required} className={className}>
      {el}
    </FormField>
  );
});

/** Search box with a clear button. */
export const SearchInput = ({ value, onChange, placeholder = "Search…", className, size = "md", ...rest }) => (
  <div className={cx("relative min-w-[180px]", className)}>
    <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-cm-muted pointer-events-none" />
    <input
      type="search"
      value={value}
      onChange={(e) => onChange?.(e.target.value, e)}
      placeholder={placeholder}
      aria-label={placeholder.replace(/…$/, "")}
      className={cx(fieldBase, sizeCls[size], "pl-9 pr-8 border-cm-border-strong [&::-webkit-search-cancel-button]:hidden")}
      {...rest}
    />
    {value ? (
      <button
        type="button"
        onClick={() => onChange?.("", null)}
        className="absolute right-2 top-1/2 -translate-y-1/2 w-6 h-6 grid place-items-center rounded text-cm-muted hover:text-cm-text"
        aria-label="Clear search"
      >
        <X size={14} />
      </button>
    ) : null}
  </div>
);

/** Checkbox with label. */
export const Checkbox = ({ label, checked, onChange, className, disabled, id, ...rest }) => {
  const autoId = useId();
  const fieldId = id || autoId;
  return (
    <label htmlFor={fieldId} className={cx("inline-flex items-center gap-2 text-sm cursor-pointer select-none", disabled && "opacity-60 cursor-not-allowed", className)}>
      <input
        id={fieldId}
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={onChange}
        className="w-4 h-4 rounded border-cm-border-strong accent-[var(--cm-gold)] cursor-pointer"
        {...rest}
      />
      {label}
    </label>
  );
};

/** On/off switch for settings. */
export const Switch = ({ checked, onChange, label, description, disabled, className }) => (
  <label className={cx("flex items-start justify-between gap-4 cursor-pointer", disabled && "opacity-60 cursor-not-allowed", className)}>
    {(label || description) && (
      <span className="min-w-0">
        {label && <span className="block text-sm font-semibold text-cm-text">{label}</span>}
        {description && <span className="block text-xs text-cm-muted mt-0.5">{description}</span>}
      </span>
    )}
    <span className="relative shrink-0 mt-0.5">
      <input
        type="checkbox"
        role="switch"
        className="peer sr-only"
        checked={!!checked}
        disabled={disabled}
        onChange={(e) => onChange?.(e.target.checked, e)}
      />
      <span className="block w-10 h-6 rounded-full bg-cm-surface-3 border border-cm-border transition-colors peer-checked:bg-cm-gold peer-focus-visible:shadow-cm-focus" />
      <span className="absolute top-1 left-1 w-4 h-4 rounded-full bg-cm-text transition-transform peer-checked:translate-x-4 peer-checked:bg-cm-gold-ink" />
    </span>
  </label>
);
